import React, { useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Box, InfoIcon, TextButton } from '@wix/design-system';
import { AppContextProvider } from '../../AppLoader';
import { GradientDivider } from './GradientDivider';
import { ThemedText } from '../../ThemedComponents';
import { ResendVerificationCode } from '../ResendVerificationCode';
import { SendErrorSectionHelper } from './SendErrorSectionHelper';
import WixAppDevice from '../../../assets/images/wixAppDevice.svg';

/**
 * Body of the redesigned Confirm-Device "Pending" screen (waiting for the push
 * approval).
 */
export const ConfirmPendingBody: React.FC = observer(() => {
  const {
    rootStore: {
      postLoginStore: {
        premiumUsers2FaEnforcementStore: { ownerAppStore: store },
      },
    },
  } = useContext(AppContextProvider);
  const { t } = useTranslation();
  useEffect(() => {
    return () => {
      store.stopPolling();
    };
  }, [store]);
  return (
    <Box
      direction="vertical"
      width="100%"
      gap={4}
      align="center"
      style={{ textAlign: 'center' }}
    >
      {store.showResendError && <SendErrorSectionHelper />}
      <Box direction="vertical" gap={1} align="center">
        <ThemedText size="medium" weight="normal">
          {t(
            'wixAppEnforcement.addOwnerApp.wizard.confirm.message.check.phone',
            { selectedDevice: store.selectedDevice?.name },
          )}
        </ThemedText>
        <div style={{ textAlign: 'center' }}>
          <ThemedText size="small" weight="thin" secondary>
            {t(
              'wixAppEnforcement.addOwnerApp.wizard.confirm.message.to.continue',
            )}
          </ThemedText>{' '}
          {store.devices.length > 1 && (
            <TextButton
              size="small"
              weight="thin"
              underline="none"
              onClick={store.onChangeDevice}
            >
              {t('wixAppEnforcement.addOwnerApp.wizard.confirm.change.device')}
            </TextButton>
          )}
        </div>
      </Box>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <div style={{ overflow: 'hidden', height: 200, width: '100%' }}>
          <WixAppDevice
            width={275}
            style={{ display: 'block', margin: '0 auto' }}
          />
        </div>
        <GradientDivider />
      </div>
      <Box
        direction="horizontal"
        gap="4px"
        align="center"
        verticalAlign="middle"
        style={{ flexWrap: 'wrap' }}
      >
        <ThemedText size="small" weight="thin">
          <span style={{ verticalAlign: 'bottom', marginInlineEnd: '4px' }}>
            {t('wixAppEnforcement.addOwnerApp.wizard.confirm.message.resend')}
          </span>
          <InfoIcon
            size="small"
            content={t(
              'wixAppEnforcement.addOwnerApp.wizard.confirm.info.tooltip',
            )}
          />
        </ThemedText>
        <ResendVerificationCode
          sendConfirmationCode={() => store.sendPushToSelectedDevice(true)}
          resendSuccessfullyIndication={store.resendSuccessfullyIndication}
          resendThrottleTimerValue={store.resendThrottleTimerValue}
          resendButtonTranslationKey="wixAppEnforcement.addOwnerApp.wizard.confirm.resend-link"
          resendIndicationTranslationKey="wixAppEnforcement.addOwnerApp.wizard.resend-indication"
          resendButtonSize="small"
          resendButtonWeight="normal"
        />
      </Box>
    </Box>
  );
});
